import React from "react";
import mainContext from "./mainContext";
import { useState } from "react";
import { notification } from "antd";
import { getCurrentFinancialYear } from "../../../ReportContainer/utils/financialYearUtils";
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const MainState = (props) => {
  const [id, setId] = useState(null);
  const [savedPageNumber, setSavedPageNumber] = useState(null);
  const [preservePageNumberOnNextListMount, setPreservePageNumberOnNextListMount] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bdfNo, setBdfNo] = useState("");
  const [soNo, setSoNo] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [salesOrders, setSalesOrders] = useState([]);
  const [fetchToggle, setFetchToggle] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchToggle, setSearchToggle] = useState(false);
  const [justView, setJustView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parties, setParties] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allParties, setAllParties] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());

  const fetchSalesOrders = async (type) => {
    setLoading(true);
    try {
      const fyParam = `&financialYear=${encodeURIComponent(financialYear)}`;
      const hasSearch =
        Boolean(searchQuery?.trim()) || Boolean(bdfNo?.trim()) || Boolean(soNo?.trim());
      const bdfParam = bdfNo?.trim()
        ? `&bdfNo=${encodeURIComponent(bdfNo.trim())}`
        : "";
      const soParam = soNo?.trim() ? `&soNo=${encodeURIComponent(soNo.trim())}` : "";
      let response = await fetch(
        `${apiBaseUrl}/inventoryManagement/listSalesOrder?search=${searchQuery}&limit=${
          hasSearch ? 1000 : pageSize
        }&page=${hasSearch ? 1 : pageNumber}&type=${type}${fyParam}${bdfParam}${soParam}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const responseData = await response.json();
      if (response.ok) {
        const formattedData =
          responseData?.data?.map((ele) => ({
            ...ele,
            billingInfo: ele?.billingInfo || [],
          })) || [];

        setSalesOrders(formattedData);
        setTotalOrders(responseData.totalOrders);
        if (!hasSearch) {
          setSearchToggle(false);
        } else {
          setSearchToggle(true);
        }
        return responseData;
      } else {
        notification.error({
          message: responseData?.message || "Failed to fetch sales orders",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Occurred while fetching sales orders",
      });
    } finally {
      setLoading(false);
    }
  };

  // Wrapper to preserve pagination when navigating back from details.
  // - When `id` becomes truthy: store current `pageNumber`.
  // - When `id` becomes falsy: restore stored page and set a flag so list views won't reset to 1 on mount.
  const handleSetId = (nextId) => {
    if (nextId) {
      setSavedPageNumber(pageNumber);
      setPreservePageNumberOnNextListMount(false);
      setId(nextId);
      return;
    }

    const hasSaved = savedPageNumber != null;
    if (hasSaved) {
      setPageNumber(savedPageNumber);
    }
    setSavedPageNumber(null);
    setPreservePageNumberOnNextListMount(hasSaved);
    setId(nextId);
  };

  const archiveSalesOrder = async (salesOrderId, deleteReason) => {
    if (!salesOrderId) {
      notification.warning({ message: "Sales Order ID is required" });
      return;
    }
    if (!deleteReason?.trim()) {
      notification.warning({ message: "Delete reason is required" });
      return;
    }

    try {
      let response = await fetch(
        `${apiBaseUrl}/inventoryManagement/archiveSalesOrder`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ salesOrderId, deleteReason: deleteReason.trim() }),
        }
      );

      const responseData = await response.json();
      if (response.ok) {
        notification.success({
          message: responseData.message || "Sales Order archived successfully",
        });
      } else {
        notification.error({
          message: responseData?.message || "Failed to archive sales order",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Occurred while archiving sales order",
      });
    }
  };

  const fetchPartiesAndBrands = async (type) => {
    try {
      let response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getSOPartiesAndBrands?type=${type}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const responseData = await response.json();
      if (response.ok) {
        setParties(responseData.parties || []);
        setBrands(responseData.brands || []);
      } else {
        notification.error({
          message:
            responseData?.message || "Failed to fetch parties and brands",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Occurred while fetching parties and brands",
      });
    }
  };

  const createBill = async (
    salesOrderId,
    partyCompany = null,
    subTotal = 0,
    duePrintingAmount,
    dueMountingAmount,
    dueAmountInvWise = null
  ) => {
    if (!salesOrderId) {
      return { success: false, message: "Sales Order ID is required" };
    }
    try {
      const body = { salesOrderId };

      if (
        partyCompany &&
        dueAmountInvWise != null &&
        Array.isArray(dueAmountInvWise)
      ) {
        body.partyCompany = partyCompany;
        body.subTotal = subTotal;
        body.duePrintingAmount = duePrintingAmount;
        body.dueMountingAmount = dueMountingAmount;
        body.dueAmountInvWise = dueAmountInvWise;
      } else if (partyCompany) {
        body.partyCompany = partyCompany;
        body.subTotal = subTotal;
        if (
          duePrintingAmount !== undefined &&
          dueMountingAmount !== undefined
        ) {
          body.duePrintingAmount = duePrintingAmount;
          body.dueMountingAmount = dueMountingAmount;
        }
      }

      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/createBill`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: responseData.message || "Bill created successfully",
        };
      } else {
        return {
          success: false,
          message: responseData?.message || "Failed to create bill",
        };
      }
    } catch (error) {
      return { success: false, message: "Error occurred while creating bill" };
    }
  };

  const fetchAllParties = async () => {
    try {
      let response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getAllParties`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const responseData = await response.json();
      if (response.ok) {
        setAllParties(responseData.parties || []);
      } else {
        notification.error({
          message: responseData?.message || "Failed to fetch all parties",
        });
        return [];
      }
    } catch (error) {
      notification.error({
        message: "Error occurred while fetching all parties",
      });
      return [];
    }
  };
  const fetchInvoiceDataForCreditNote = async (invoiceId) => {
    try {
      if (invoiceId) {
        let response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getInvoiceDataById?invoiceId=${invoiceId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const responseData = await response.json();

        if (response.ok) {
          const invoiceInfo = responseData.invoiceInfo.salesOrderId || {};
          const bdfId = invoiceInfo?.bdfId || {};
          const partyCompany = bdfId?.parentCompanyId || {};
          const billedInvs = responseData.invoiceInfo?.inventories || [];
          const billedSuffixInvs =
            responseData.invoiceInfo?.suffixInventories || [];

          // console.log("😿🟢🟢", invoiceInfo);
          setInvoiceData({
            // billingId: id,
            // salesOrderId: invoiceInfo?._id,
            HSN: responseData?.invoiceInfo?.HSN,
            IRN: responseData?.invoiceInfo?.IRN,
            refNo: responseData?.invoiceInfo?.refNo,
            refDate: responseData?.invoiceInfo?.refDate,
            isRejected: responseData?.invoiceInfo?.isRejected,
            cancelReason: responseData?.invoiceInfo?.cancelremark,
            bdfId: bdfId?._id,
            salesPerson: bdfId?.salesPerson,
            bdfStamp: bdfId?.stamp,
            gstNumber: partyCompany?.gstNumber || null,
            parentCompanyAddress: partyCompany?.address || null,
            stamp: invoiceInfo.stamp || null,
            roNumber: invoiceInfo.roNumber || null,
            parentCompanyName: bdfId?.parentCompanyId?.name || bdfId?.parentCompanyName || null,
            splitParentCompanyName:
              responseData?.invoiceInfo?.billingId?.partyCompany?.name || null,
            splitGSTNumber:
              responseData?.invoiceInfo?.billingId?.partyCompany?.gstNumber ||
              null,
            splitParentCompanyAddress:
              responseData?.invoiceInfo?.billingId?.partyCompany?.address ||
              null,
            splitAmount: responseData?.invoiceInfo?.billingId?.subTotal || null,
            aliasPartyName: responseData?.invoiceInfo?.aliasPartyName || null,
            remark: bdfId?.remark || null,
            brandName: invoiceInfo?.bdfId?.brandId?.brandName || null,
            agencyCommission: invoiceInfo?.agencyCommission || 0,
            campaignDuration: bdfId?.campaignDuration || null,
            bdfType: bdfId?.bdfType,
            printingCharge: responseData?.invoiceInfo?.printingCharge || 0,
            mountingCharge: responseData?.invoiceInfo?.mountingCharge || 0,
            createdAt: responseData?.invoiceInfo?.createdAt || 0,
            createdBy: responseData?.invoiceInfo?.createdBy?.name,
            AckDate: responseData?.invoiceInfo?.AckDate || null,
            AckNo: responseData?.invoiceInfo?.AckNo || null,
            IRN: responseData?.invoiceInfo?.IRN || null,
            billNumber: responseData?.invoiceInfo?.billNumber || null,
            billedInvs:
              billedInvs?.map((inv) => ({
                _id: inv?.invId?._id || null,
                amount: inv?.amount || null,
                startDate: inv?.startDate || null,
                endDate: inv?.endDate || null,
                height: inv?.invId?.height || null,
                width: inv?.invId?.width || null,
                locationName: inv?.invId?.locations?.name || null,
                CodeNo: inv?.invId?.CodeNo || null,
                unit: inv?.invId?.unit || null,
                isTrading: inv?.invId?.isTrading || false,
                ownedBy: inv?.invId?.ownedBy || null,
              })) || [],
            inventories: bdfId?.inventories
              ? bdfId?.inventories
                  .filter((inv) => {
                    // Filter inventories to match those in billedInvs by their _id
                    return billedInvs.some(
                      (billedInv) => billedInv?.invId?._id === inv?.invId?._id
                    );
                  })
                  .map((inv) => ({
                    _id: inv?.invId?._id || false,
                    locationName: inv?.invId?.locations?.name || null,
                    CodeNo: inv?.invId?.CodeNo || null,
                    suffix: inv?.invId?.suffix || "HO",
                    isTrading: inv?.invId?.isTrading || false,
                    ownedBy: inv?.invId?.ownedBy || false,
                    rpm: inv?.rpm || null,
                    printingCharge: inv?.printingCharge || 0,
                    mountingCharge: inv?.mountingCharge || 0,
                    specialRate: inv?.specialRate || null,
                    specialRatePerMonth: inv?.specialRatePerMonth || 0,
                    startDate: inv?.startDate || null,
                    endDate: inv?.endDate || null,
                  }))
              : [],
            billedSuffixInvs:
              billedSuffixInvs?.map((inv) => ({
                _id: inv?.invId || null,
                startDate: inv?.startDate || null,
                endDate: inv?.endDate || null,
                amount: inv?.amount || null,
              })) || [],
            suffixInventories: bdfId?.suffixInventories
              ? bdfId?.suffixInventories
                  .filter((inv) => {
                    // Filter suffixInventories to match those in billedSuffixInvs by their _id
                    return billedSuffixInvs.some(
                      (billedInv) => billedInv?.invId === inv?._id
                    );
                  })
                  .map((inv) => ({
                    _id: inv?._id,
                    specialRate: inv?.specialRate || 0,
                    purchaseRate: inv?.purchaseRate || 0,
                    suffixCode: inv?.suffixCode || null,
                    suffixInfo: inv?.suffixInfo || null,
                    vendor: inv?.vendor || null,
                  }))
              : [],
            //taxes
            taxType: invoiceInfo?.taxType || 0,
            IGST: responseData?.invoiceInfo?.IGST || 0,
            SGST: responseData?.invoiceInfo?.SGST || 0,
            CGST: responseData?.invoiceInfo?.CGST || 0,
            municipalTax: invoiceInfo?.municipalTax || 0,
            otherTaxes: invoiceInfo?.otherTaxes || 0,
            round: invoiceInfo?.round || 0,
            isFromSub: Boolean(invoiceInfo?.isFromSub),
          });

          // let totalRate = bdfId?.inventories?.reduce((acc, next) => {
          //   return (
          //     acc +
          //     (Number(next?.specialRate) || 0) +
          //     (Number(next?.mountingCharge) || 0) +
          //     (Number(next?.printingCharge) || 0) +
          //     (Number(next?.innovationCharge) || 0)
          //   ); // Ensures specialRate is a number, defaults to 0 if undefined
          // }, 0);

          // if (bdfId?.suffixInventories?.length > 0) {
          //   totalRate += bdfId.suffixInventories.reduce(
          //     (acc, next) => acc + (Number(next?.specialRate) || 0),
          //     0
          //   );
          // }

          // Now calculate the subTotal
          let totalRate = 0;

          // Filter the inventories to match those in billedInvs
          const filteredInventories = bdfId?.inventories.filter((inv) => {
            return billedInvs.some(
              (billedInv) => billedInv?.invId?._id === inv?.invId?._id
            );
          });

          // Sum up the required charges and amount
          filteredInventories.forEach((inv) => {
            const matchedBilledInv = billedInvs.find(
              (billedInv) => billedInv?.invId?._id === inv?.invId?._id
            );
            if (matchedBilledInv) {
              totalRate +=
                // (Number(inv?.mountingCharge) || 0) +
                // (Number(inv?.printingCharge) || 0) +
                (Number(inv?.innovationCharge) || 0) +
                (Number(matchedBilledInv?.amount) || 0);
            }
          });

          // Add billedSuffixInvs amounts if present
          if (billedSuffixInvs?.length > 0) {
            billedSuffixInvs.forEach((suffixInv) => {
              totalRate += Number(suffixInv?.amount) || 0;
            });
          }

          // ✅ Add overall invoice-level mounting and printing charges
          totalRate +=
            (Number(responseData?.invoiceInfo?.mountingCharge) || 0) +
            (Number(responseData?.invoiceInfo?.printingCharge) || 0);

          // setSubTotal(totalRate);

          // return responseData;
        } else {
          notification.error({
            message: responseData?.message || "Failed to fetch the sales order",
          });
        }
      }
    } catch (error) {
      notification.error({
        message: error.message,
      });
    }
  };

  return (
    <mainContext.Provider
      value={{
        id,
        setId: handleSetId,
        searchQuery,
        setSearchQuery,
        bdfNo,
        setBdfNo,
        soNo,
        setSoNo,
        pageSize,
        setPageSize,
        pageNumber,
        setPageNumber,
        preservePageNumberOnNextListMount,
        setPreservePageNumberOnNextListMount,
        fetchSalesOrders,
        fetchToggle,
        setFetchToggle,
        totalOrders,
        searchToggle,
        salesOrders,
        setSalesOrders,
        justView,
        setJustView,
        loading,
        archiveSalesOrder,
        fetchPartiesAndBrands,
        financialYear,
        setFinancialYear,
        parties,
        brands,
        createBill,
        fetchAllParties,
        allParties,
        fetchInvoiceDataForCreditNote,
      }}
    >
      {props.children}
    </mainContext.Provider>
  );
};

export default MainState;
